// Pure, dependency-free logic for the Day 0 Baseline step of a Findability Cycle.
// API functions are injected so this stays trivially unit-testable (mirrors cycleFlow.js).

// Canonical Baseline self-assessment questions — reused verbatim from the existing
// "Test Your Findability" self-check (FindabilityCheck.jsx). Do NOT invent new questions.
export const BASELINE_QUESTIONS = [
  { key: "q1", text: "Did it name you or your work specifically?" },
  { key: "q2", text: "Did it use the specific words or approach only you use — your method, your process, your language?" },
  { key: "q3", text: "Or did it default to generic advice for the broader category?" },
  { key: "q4", text: "Did it name other specific people, brands, or tools as the go-to answer?" },
];

export const REQUIRED_ANSWER_KEYS = ["q1", "q2", "q3", "q4"];

// Which required Baseline fields are still missing (Day 0 findings note + all four answers).
export function missingBaselineFields({ note, answers } = {}) {
  const missing = [];
  if (!(note || "").trim()) missing.push("note");
  const a = answers || {};
  REQUIRED_ANSWER_KEYS.forEach((k) => {
    if (!String(a[k] || "").trim()) missing.push(k);
  });
  return missing;
}

export const isBaselineComplete = (draft) => missingBaselineFields(draft).length === 0;

// Only a fully-activated pre-Baseline cycle (no activation recovery still pending) should show the
// Baseline screen. Mirrors the customer-safe lifecycle projection.
export function isPreBaseline(lifecycle) {
  const c = lifecycle && lifecycle.current_cycle;
  return (
    !!c &&
    c.status === "activating_baseline_pending" &&
    c.activation_recovery_pending === false
  );
}

// Backend error codes -> plain customer-facing copy. Internal codes are never surfaced.
const MESSAGES = {
  baseline_incomplete: "Please add your baseline findings and answer all four questions before confirming.",
  baseline_missing: "Please add your baseline findings and answer all four questions before confirming.",
  no_active_cycle: "We couldn't find a cycle waiting for its baseline. Please refresh and try again.",
  activation_recovery_pending: "Your cycle is still finishing setup. Please try again in a moment.",
  baseline_already_confirmed: "Your baseline is already confirmed, so it can no longer be edited.",
  baseline_not_editable: "Your baseline can no longer be edited.",
  baseline_invariant_violation: "This account needs a quick review before continuing. Please contact support.",
  cycle_terminal: "This cycle is closed.",
  not_owned: "Something went wrong. Please try again.",
};

export function friendlyBaselineError(err) {
  const detail = err && err.response && err.response.data && err.response.data.detail;
  const code = detail && detail.code;
  return MESSAGES[code] || "Something went wrong. Please try again.";
}

// Build the exact Baseline payload the backend accepts. Required: note + q1..q4 answers.
// Optional (already part of the Day 0 experience): phrases + source_title + source_audience.
export function buildBaselinePayload({ note, answers, phrases, sourceTitle, sourceAudience } = {}) {
  const payload = {
    note: (note || "").trim(),
    answers: REQUIRED_ANSWER_KEYS.reduce((acc, k) => {
      acc[k] = (answers && answers[k]) || "";
      return acc;
    }, {}),
  };
  const cleanedPhrases = (phrases || []).map((p) => String(p).trim()).filter(Boolean);
  if (cleanedPhrases.length) payload.phrases = cleanedPhrases;
  if ((sourceTitle || "").trim()) payload.source_title = sourceTitle.trim();
  if ((sourceAudience || "").trim()) payload.source_audience = sourceAudience.trim();
  return payload;
}

// Save Draft: PUT the draft ONLY. Never confirms, never re-fetches lifecycle, never advances state.
export async function runSaveDraft({ payload, saveBaselineDraft }) {
  try {
    const result = await saveBaselineDraft(payload);
    return { ok: true, result };
  } catch (e) {
    return { ok: false, message: friendlyBaselineError(e) };
  }
}

// Confirm: canonical POST confirm ONLY, then re-fetch the lifecycle so the caller renders the next
// screen from the authoritative lifecycle result (the frontend never forces the cycle state itself).
export async function runConfirm({ confirmBaseline, getLifecycle }) {
  try {
    await confirmBaseline();
    const lifecycle = await getLifecycle();
    return { ok: true, lifecycle };
  } catch (e) {
    return { ok: false, message: friendlyBaselineError(e) };
  }
}


// Interpret a cycle-bound phrase job poll response into a UI-friendly shape (pure/testable).
export function interpretPhraseJob(res) {
  const r = res || {};
  if (r.status === "done") {
    return {
      done: true,
      phrases: Array.isArray(r.phrases) ? r.phrases : [],
      sourceTitle: r.source_title || "",
      sourceAudience: r.source_audience || "",
    };
  }
  if (r.status === "failed" || r.status === "busy") {
    return { done: true, error: r.message || "The studio is busy right now. Your work is saved — try again in a few minutes." };
  }
  if (r.status === "queued" && r.position > 1) {
    return { done: false, queueMsg: `You're #${r.position} in line — hang tight, your phrases are moments away.` };
  }
  return { done: false, queueMsg: "Finding your phrases…" };
}


// Normalize a canonical Baseline draft (from GET /cycle/baseline) into component state (pure/testable).
export function hydrateFromDraft(draft) {
  const d = draft || {};
  return {
    phrases: Array.isArray(d.phrases) ? d.phrases : [],
    note: d.note || "",
    answers: d.answers && typeof d.answers === "object" ? d.answers : {},
    sourceTitle: d.source_title || "",
    sourceAudience: d.source_audience || "",
  };
}
